<script setup lang="ts">
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { IRegularAvailabilities, WorkerPreferences } from '@/types/worker';

import AvailabilityTable from "@/dsComponents/Availability/AvailabilityTable.vue";
import FormSection from "@/dsComponents/FormSection.vue";

import AvailabilityToggleCard from "./AvailabilityToggleCard.vue";

const { t } = useI18n();

interface WorkerAvailabilitiesProps {
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    worker: any;
    edition?: boolean;
    readonly: boolean;
    showFormSectionSeparator?: boolean;
    compactAvailabilityColumnLabels?: boolean;
    showFormSectionSubtitle?: boolean;
}
const props = withDefaults(defineProps<WorkerAvailabilitiesProps>(), {
    edition: false,
    showFormSectionSeparator: true,
    compactAvailabilityColumnLabels: false,
    showFormSectionSubtitle: true,
});

const preferences = reactive<WorkerPreferences>({
    environment: props.worker.environment,
    contractPreference: props.worker.contractPreference,
    regularAvailabilities: props.worker.regularAvailabilities,
    weekendShift: props.worker.weekendShift,
    nightShift: props.worker.nightShift,
    schedule: props.worker.schedule,
    vehicle: props.worker.vehicle,
    maxTravelDistance: props.worker.maxTravelDistance,
    maxTravelTime: props.worker.maxTravelTime,
    carpooling: props.worker.carpooling,
    teamPreference: props.worker.teamPreference,
});

const handleRegularAvailabiltiesChange = (availabilities: IRegularAvailabilities[]) => {
    preferences.regularAvailabilities = availabilities;
};

const handleWeekendShift = () => {
    preferences.weekendShift = !preferences.weekendShift;
    preferences.regularAvailabilities = preferences.regularAvailabilities.map((availability: IRegularAvailabilities, index: number) => {
        if (index === 5 || index === 6) {
            availability.morning = preferences.weekendShift;
            availability.afternoon = preferences.weekendShift;
            availability.evening = preferences.weekendShift;
            availability.night = preferences.weekendShift;
        }
        return availability;
    });
};

const handleNightShift = () => {
    preferences.nightShift = !preferences.nightShift;
    preferences.regularAvailabilities = preferences.regularAvailabilities.map((availability: IRegularAvailabilities) => {
        availability.night = preferences.nightShift;
        return availability;
    });
};

const emit = defineEmits(["update"]);

const subtitle = computed(() => (props.edition ? t("page.interim.regularAvailabilities.info.edition") : ""));

watch(preferences, () => {
    emit("update", { ...preferences });
    const weekend = [5, 6];
    const allWeekendTrue = weekend.every((index) => {
        const availability = preferences.regularAvailabilities[index];
        return availability && Object.values(availability).every((value) => value);
    });
    const allNightTrue = preferences.regularAvailabilities.every((availability) => availability.night === true);

    preferences.weekendShift = allWeekendTrue;
    preferences.nightShift = allNightTrue;
});
</script>
<template>
    <div class="worker-preferences" :class="{ 'worker-preferences--aside': compactAvailabilityColumnLabels }">
        <FormSection
            :title="compactAvailabilityColumnLabels ? undefined : t('page.interim.regularAvailabilities.title')"
            :subtitle="showFormSectionSubtitle ? subtitle : false"
            size="md"
            :separator="showFormSectionSeparator"
        >
            <template v-if="compactAvailabilityColumnLabels" #title>
                <h2 class="worker-preferences__section-title typo-sm-medium">
                    {{ t("page.interim.regularAvailabilities.title") }}
                </h2>
            </template>
        </FormSection>
        <div
            class="worker-preferences__after-form-section"
            :class="{ 'worker-preferences__after-form-section--interview': compactAvailabilityColumnLabels }"
        >
            <div v-if="edition" class="toggle-cards">
                <AvailabilityToggleCard
                    :label="t('page.interim.regularAvailabilities.weekendshift')"
                    :active="preferences.weekendShift"
                    :disabled="readonly"
                    @input="handleWeekendShift"
                />
                <AvailabilityToggleCard
                    :label="t('page.interim.regularAvailabilities.nightShift')"
                    :active="preferences.nightShift"
                    :disabled="readonly"
                    @input="handleNightShift"
                />
            </div>
            <AvailabilityTable
                :edition="edition"
                :regular-availabilities="preferences.regularAvailabilities"
                :compact-column-labels="compactAvailabilityColumnLabels"
                @input="handleRegularAvailabiltiesChange"
            />
        </div>
    </div>
</template>
<style scoped lang="scss">
.worker-preferences {
    max-width: 600px;

    &--aside {
        max-width: none;
    }

    &__section-title {
        margin: 0;
        color: var(--neutral-700, #323848);
    }

    :deep(.ds-form-section) {
        margin-bottom: 28px;
    }

    &__after-form-section {
        width: 100%;

        .toggle-cards {
            margin-bottom: var(--spacing-md, 8px);
        }

        &--interview {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-md, 8px);

            .toggle-cards {
                margin-bottom: 0;
            }
        }
    }

    .toggle-cards {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md, 8px);
    }
}
</style>
